/**
 * Dashboards list view — /dashboards
 *
 * The shared list-page pattern (`useListControls` + `ListToolbar` + `VirtualizedCards` / `Table`)
 * applied to dashboards: search, a card/list view toggle, sort, and a visibility filter. Delete is
 * confirmed via a dialog.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutDashboard } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/_shared/components/ConfirmDialog';
import { ListEmpty, ListError, ListNoMatches, ListSkeleton } from '@/_shared/components/list/ListStates';
import { ListToolbar } from '@/_shared/components/list/ListToolbar';
import { VirtualizedCards } from '@/_shared/components/list/VirtualizedCards';
import { Table } from '@/_shared/components/Table';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useChallengeCreators } from '@/_shared/hooks/useChallengeCreators';
import { useListControls } from '@/_shared/hooks/useListControls';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { useStarred } from '@/_shared/hooks/useStarred';
import { filterSortItems, type SortState } from '@/_shared/lib/list-controls';

import { DashboardCard } from '../components/DashboardCard';
import { buildDashboardColumns } from '../components/dashboardColumns';
import {
  DASHBOARD_DEFAULT_SORT,
  DASHBOARD_DERIVE_CONFIG,
  DASHBOARD_FILTER_GROUPS,
  DASHBOARD_FILTER_KEYS,
  DASHBOARD_FILTER_VALUES,
  DASHBOARD_SORT_KEYS,
  DASHBOARD_SORT_OPTIONS,
  type DashboardFilterKey,
  type DashboardListViewItem,
  type DashboardSortKey,
} from '../lib/dashboardList';

export function DashboardList(): React.ReactElement {
  usePageTitle('Dashboards');
  const { wp } = useWorkspace();
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showCreators, nameFor } = useChallengeCreators();

  const { data, isLoading, error } = useQuery(orpc.dashboard.list.queryOptions({ input: {} }));
  const { isStarred, toggleStar, isToggling } = useStarred('dashboard');
  const dashboards = useMemo<DashboardListViewItem[]>(
    () => (data?.dashboards ?? []).map((dashboard) => ({ ...dashboard, isStarred: isStarred(dashboard.id) })),
    [data?.dashboards, isStarred],
  );

  const controls = useListControls<DashboardSortKey, DashboardFilterKey>({
    sortKeys: DASHBOARD_SORT_KEYS,
    defaultSort: DASHBOARD_DEFAULT_SORT,
    filterKeys: DASHBOARD_FILTER_KEYS,
    filterOptions: DASHBOARD_FILTER_VALUES,
    persistKey: 'dashboards',
  });
  // `controls.query` is already debounced by the toolbar's search box, so this recomputes on the debounce,
  // not per keystroke.
  const visible = useMemo(
    () =>
      filterSortItems(
        dashboards,
        { query: controls.query, sort: controls.sort, filters: controls.filters },
        DASHBOARD_DERIVE_CONFIG,
      ),
    [dashboards, controls.query, controls.sort, controls.filters],
  );

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const deletingDashboard = dashboards.find((d) => d.id === deletingId) ?? null;
  const onDelete = useCallback((id: string) => setDeletingId(id), []);
  const deleteMutation = useMutation(
    orpc.dashboard.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: orpc.dashboard.list.queryOptions({ input: {} }).queryKey });
        toast.success('Dashboard deleted');
        setDeletingId(null);
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Failed to delete dashboard');
      },
    }),
  );

  // Null outside challenge mode, so the card/column renderers gate creator attribution on one value.
  const creatorNameFor = showCreators ? nameFor : null;
  const columns = useMemo(
    () =>
      buildDashboardColumns({
        onDelete,
        creatorNameFor,
        onToggleStar: toggleStar,
        isStarPending: isToggling,
      }),
    [onDelete, creatorNameFor, toggleStar, isToggling],
  );

  const getRowId = useCallback((d: DashboardListViewItem) => d.id, []);
  const openDashboard = useCallback(
    (d: DashboardListViewItem) => {
      void navigate(wp(`/d/${d.slug}`));
    },
    [navigate, wp],
  );
  const renderCard = useCallback(
    (d: DashboardListViewItem) => (
      <DashboardCard
        dashboard={d}
        to={wp(`/d/${d.slug}`)}
        onDelete={onDelete}
        creatorName={creatorNameFor?.(d.createdBy) ?? null}
        onToggleStar={toggleStar}
        starPending={isToggling(d.id)}
      />
    ),
    [wp, onDelete, creatorNameFor, toggleStar, isToggling],
  );

  const hasDashboards = dashboards.length > 0;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <header className="border-b px-6 py-4">
        <h1 className="text-lg font-semibold tracking-tight text-foreground">Dashboards</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">Analytics dashboards generated from your conversations.</p>
      </header>

      {error && !hasDashboards ? (
        // Only take over the whole page when there's nothing to show — a failed background refetch keeps
        // the cached list usable, so don't blank it behind a full-screen error.
        <ListError
          title="Couldn't load dashboards"
          message={error instanceof Error ? error.message : 'Something went wrong.'}
        />
      ) : isLoading ? (
        <ListSkeleton view={controls.view} />
      ) : !hasDashboards ? (
        <ListEmpty
          icon={LayoutDashboard}
          title="No dashboards yet"
          description="Create one by starting a chat conversation and asking the assistant to build a dashboard for you."
        />
      ) : (
        <>
          <div className="pt-4 pb-1">
            <div className="mx-auto max-w-5xl px-6">
              <ListToolbar
                query={controls.query}
                onQueryChange={controls.setQuery}
                searchPlaceholder="Search dashboards…"
                view={controls.view}
                onViewChange={controls.setView}
                sortOptions={DASHBOARD_SORT_OPTIONS}
                sort={controls.sort}
                onSortChange={(next) => controls.setSort(next as SortState<DashboardSortKey>)}
                defaultSort={DASHBOARD_DEFAULT_SORT}
                filterGroups={DASHBOARD_FILTER_GROUPS}
                filters={controls.filters}
                onFilterChange={(key, value) => controls.setFilter(key as DashboardFilterKey, value)}
                onClearFilters={controls.clearFilters}
                onClearAll={controls.clearAll}
                totalCount={dashboards.length}
                resultCount={visible.length}
                itemNoun={{ singular: 'dashboard', plural: 'dashboards' }}
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
            {visible.length === 0 ? (
              <ListNoMatches
                noun="dashboards"
                onClear={() => {
                  controls.setQuery('');
                  controls.clearFilters();
                }}
              />
            ) : controls.view === 'list' ? (
              <div className="mx-auto h-full max-w-5xl px-6 py-4">
                <Table
                  data={visible}
                  columns={columns}
                  getRowId={getRowId}
                  onRowClick={openDashboard}
                  hideHeader
                  fixedLayout
                  fillHeight
                  emptyState="No dashboards"
                />
              </div>
            ) : (
              <VirtualizedCards
                items={visible}
                getItemKey={getRowId}
                minColumnRem={20}
                maxColumns={3}
                maxContentRem={64}
                estimateRowHeight={152}
                className="h-full"
                renderItem={renderCard}
              />
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={deletingId !== null}
        onOpenChange={(next) => {
          if (!next) setDeletingId(null);
        }}
        title="Delete this dashboard?"
        description={
          <>
            This permanently deletes {deletingDashboard ? `"${deletingDashboard.title}"` : 'the dashboard'} and its
            panels. Any shared link stops working. This can&apos;t be undone.
          </>
        }
        loading={deleteMutation.isPending}
        onConfirm={() => deletingId && deleteMutation.mutate({ id: deletingId })}
      />
    </div>
  );
}
